import { api } from './api';

// WhatsApp Employee Inbox API (Phase 1 D) — tenant-scoped read/reply.
//
// Cross-tenant note: when a thread is opened from a CAMPAIGN contact panel, the
// campaign may belong to a different tenant than the caller (super_admin / org
// admin viewing another tenant's campaign). Passing `campaignId` lets the
// backend resolve the campaign's real tenant (via _resolve_campaign_tenant) so
// the thread partition is keyed to the campaign's tenant instead of the
// caller's own — otherwise the panel loads an empty thread ("no messages" +
// "24h window closed"). Omitting campaignId keeps the tenant-scoped inbox
// behavior unchanged.

// Build query params, adding campaign_id only when provided (non-empty).
const withCampaign = (params = {}, campaignId) => {
  const cid = (campaignId || '').trim();
  return cid ? { ...params, campaign_id: cid } : params;
};

// WA-DEPARTMENT-CONTEXT-SPLIT-aug25: add department only when provided so a
// single phone's ventas vs postventa conversations resolve to distinct
// partitions. Omitting it keeps the legacy (ventas-default) behavior.
const withDepartment = (params = {}, department) => {
  const dept = (department || '').trim();
  return dept ? { ...params, department: dept } : params;
};

// List the current tenant's WhatsApp threads (most recent activity first).
export const listWhatsappThreads = (params = {}) =>
  api.get('/whatsapp/threads', { params });

// Fetch one thread (messages + meta). Opening resets the unread counter.
// ``params.department`` (ventas|postventa) selects the conversation partition.
export const getWhatsappThread = (leadPhone, params = {}, campaignId) =>
  api.get(`/whatsapp/threads/${encodeURIComponent(leadPhone)}`, {
    params: withCampaign(params, campaignId),
  });

// Reply to a thread.
//  - Inside the 24h window: pass { text }.
//  - Outside the window: pass { template_sid, vars }.
//  - ``department`` (ventas|postventa) targets the right conversation partition.
export const replyWhatsappThread = (leadPhone, body, campaignId, department) =>
  api.post(`/whatsapp/threads/${encodeURIComponent(leadPhone)}/reply`, body, {
    params: withDepartment(withCampaign({}, campaignId), department),
  });

// Toggle the per-thread AI responder on/off (persists on the thread META item).
// ``department`` (ventas|postventa) selects which thread's toggle is updated.
export const setWhatsappThreadAgent = (leadPhone, enabled, campaignId, department) =>
  api.put(
    `/whatsapp/threads/${encodeURIComponent(leadPhone)}/agent`,
    { enabled },
    { params: withDepartment(withCampaign({}, campaignId), department) },
  );
